import { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Lock, CheckCircle, AlertCircle, ArrowLeft } from "lucide-react";
import { PasswordInput } from "../components/shared/PasswordInput";
import { TelcaVoIPBadge } from "../components/shared/TelcaVoIPBadge";
import { toast } from "sonner";
import axios from "axios";
import { API, LOJACK_LOGO } from "../config";

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  // Forgot password form (no token)
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  // Reset form (with token)
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const handleForgot = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/dealer/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      toast.error("Errore nell'invio");
    } finally { setLoading(false); }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    if (password.length < 6) { setError("Password minimo 6 caratteri"); return; }
    if (password !== confirm) { setError("Le password non coincidono"); return; }
    setLoading(true); setError("");
    try {
      await axios.post(`${API}/dealer/reset-password`, { token, password });
      setDone(true);
      toast.success("Password aggiornata!");
    } catch (err) {
      setError(err.response?.data?.detail || "Link non valido o scaduto.");
    } finally { setLoading(false); }
  };

  // Reset with token
  if (token) {
    if (done) return (
      <div className="min-h-screen bg-lojack-black flex items-center justify-center">
        <div className="text-center">
          <CheckCircle className="w-16 h-16 text-lojack-blue-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Password aggiornata!</h2>
          <button onClick={() => navigate("/dealer")} className="btn-lojack mt-4">Vai al Login</button>
        </div>
      </div>
    );

    return (
      <div className="min-h-screen bg-lojack-black flex items-center justify-center px-4" data-testid="reset-password-page">
        <div className="w-full max-w-md">
          <div className="flex items-center justify-center gap-3 mb-8">
            <img src={LOJACK_LOGO} alt="LoJack Tracker" className="h-12" />
          </div>
          <div className="bg-[#111] border border-gray-800 rounded-2xl p-8">
            <h1 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
              <Lock className="text-lojack-red" size={20} /> Reimposta Password
            </h1>
            {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 text-sm rounded-xl px-4 py-3 mb-4">{error}</div>}
            <form onSubmit={handleReset} className="space-y-4">
              <div>
                <label className="text-gray-400 text-xs">Nuova Password *</label>
                <PasswordInput value={password} onChange={e => setPassword(e.target.value)} required minLength={6}
                  className="w-full mt-1 px-4 py-3 pr-12 bg-[#0A0A0A] border border-gray-700 rounded-xl text-white focus:border-lojack-red focus:outline-none" data-testid="reset-password-input" />
              </div>
              <div>
                <label className="text-gray-400 text-xs">Conferma Password *</label>
                <PasswordInput value={confirm} onChange={e => setConfirm(e.target.value)} required
                  className="w-full mt-1 px-4 py-3 pr-12 bg-[#0A0A0A] border border-gray-700 rounded-xl text-white focus:border-lojack-red focus:outline-none" data-testid="reset-confirm-input" />
              </div>
              <button type="submit" disabled={loading} className="w-full btn-lojack py-3 disabled:opacity-50" data-testid="reset-submit">
                {loading ? "Salvataggio..." : "Salva Nuova Password"}
              </button>
            </form>
          </div>
          <div className="text-center mt-6"><TelcaVoIPBadge /></div>
        </div>
      </div>
    );
  }

  // Forgot password form (no token)
  if (sent) return (
    <div className="min-h-screen bg-lojack-black flex items-center justify-center">
      <div className="text-center max-w-md">
        <CheckCircle className="w-16 h-16 text-lojack-blue-500 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-white mb-2">Email inviata!</h2>
        <p className="text-gray-400 mb-6">Se l'indirizzo e' registrato, riceverai un link per reimpostare la password.</p>
        <Link to="/dealer" className="text-lojack-red hover:underline">Torna al login</Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-lojack-black flex items-center justify-center px-4" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-3 mb-8">
          <img src={LOJACK_LOGO} alt="LoJack Tracker" className="h-12" />
        </div>
        <div className="bg-[#111] border border-gray-800 rounded-2xl p-8">
          <h1 className="text-xl font-bold text-white mb-2">Password dimenticata?</h1>
          <p className="text-gray-400 text-sm mb-6">Inserisci la tua email e ti invieremo un link per reimpostarla.</p>
          <form onSubmit={handleForgot} className="space-y-4">
            <div>
              <label className="text-gray-400 text-xs">Email *</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} required
                className="w-full mt-1 px-4 py-3 bg-[#0A0A0A] border border-gray-700 rounded-xl text-white focus:border-lojack-red focus:outline-none"
                placeholder="La tua email" data-testid="forgot-email-input" />
            </div>
            <button type="submit" disabled={loading} className="w-full btn-lojack py-3 disabled:opacity-50" data-testid="forgot-submit">
              {loading ? "Invio..." : "Invia Link Reset"}
            </button>
          </form>
          <div className="mt-4 text-center">
            <Link to="/dealer" className="text-gray-500 hover:text-white text-sm flex items-center justify-center gap-1">
              <ArrowLeft size={14} /> Torna al login
            </Link>
          </div>
          <div className="text-center mt-6"><TelcaVoIPBadge /></div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
